import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useSeo } from "../../lib/seo";
import { PhoneInput } from "../../components/PhoneInput";

interface PublicCat {
  id: string;
  active: boolean;
  missing: boolean;
  name: string | null;
  motto: string | null;
  steckbrief: string | null;
  photos: { url: string; isPrimary: boolean }[];
}

type Step = "choose" | "share" | "write";
type GpsState = "idle" | "asking" | "ok" | "denied";
type FindingState = "none" | "submitting" | "done";

export function FinderPage() {
  useSeo({ title: "Tier gefunden? Besitzer benachrichtigen", noindex: true });
  const { id = "" } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ["public-cat", id],
    queryFn: () => api<PublicCat>(`/api/public/pet/${id}`),
    enabled: !!id,
  });

  const [step, setStep] = useState<Step>("choose");
  const [gps, setGps] = useState<GpsState>("idle");
  const [coords, setCoords] = useState<{ lat: number; lng: number; accuracyM: number } | null>(null);
  const [address, setAddress] = useState("");
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [finding, setFinding] = useState<FindingState>("none");
  const [findingId, setFindingId] = useState<string | null>(null);
  const [followupSending, setFollowupSending] = useState(false);
  const [followupSent, setFollowupSent] = useState(false);
  const [followupOpen, setFollowupOpen] = useState(false);
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Thumbnail für das gewählte Foto statt des nutzlosen Dateinamens.
  useEffect(() => {
    if (!photo) {
      setPhotoPreview(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const requestGps = () => {
    setGps("asking");
    setCoords(null);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setCoords({ lat: p.coords.latitude, lng: p.coords.longitude, accuracyM: p.coords.accuracy });
        setGps("ok");
      },
      () => setGps("denied"),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 },
    );
  };

  const startShare = () => {
    setStep("share");
    if (gps === "idle") requestGps();
  };

  const doSubmit = async () => {
    if (finding !== "none") return;
    setFinding("submitting");
    setError(null);
    try {
      const form = new FormData();
      if (coords) {
        form.set("lat", String(coords.lat));
        form.set("lng", String(coords.lng));
        form.set("accuracyM", String(coords.accuracyM));
      } else if (address.trim()) {
        form.set("address", address.trim());
      }
      if (message.trim()) form.set("message", message.trim());
      if (phone.trim()) {
        form.set("finderPhone", phone.trim());
        form.set("finderWhatsapp", String(whatsapp));
      }
      if (photo) form.set("photo", photo);
      const res = await api<{ ok: boolean; id: string; distanceKm: number | null }>(
        `/api/public/pet/${id}/finding`,
        { method: "POST", body: form },
      );
      setDistanceKm(res.distanceKm ?? null);
      setFindingId(res.id);
      // Felder leeren — auf der Erfolgsseite dienen sie dem Nachreichen (PATCH).
      setMessage("");
      setPhoto(null);
      setPhone("");
      setWhatsapp(false);
      setFinding("done");
    } catch {
      setFinding("none");
      setError(t("common.error"));
    }
  };

  // Ergänzung (Nachricht/Foto) an die bestehende Fundmeldung anhängen —
  // bewusst PATCH statt neuer Meldung, damit der Besitzer nicht doppelt
  // benachrichtigt wird (Server: 24h-TTL, keine Re-Notification).
  const doFollowup = async () => {
    if (!findingId || followupSending || (!message.trim() && !photo && !phone.trim())) return;
    setFollowupSending(true);
    setError(null);
    try {
      const form = new FormData();
      if (message.trim()) form.set("message", message.trim());
      if (phone.trim()) {
        form.set("finderPhone", phone.trim());
        form.set("finderWhatsapp", String(whatsapp));
      }
      if (photo) form.set("photo", photo);
      await api(`/api/public/pet/${id}/finding/${findingId}`, { method: "PATCH", body: form });
      setMessage("");
      setPhoto(null);
      setPhone("");
      setWhatsapp(false);
      setFollowupSent(true);
    } catch {
      setError(t("common.error"));
    } finally {
      setFollowupSending(false);
    }
  };

  if (isLoading || !data) {
    return <div className="min-h-dvh flex items-center justify-center text-slate-500">…</div>;
  }

  if (!data.active) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-6 text-center bg-slate-50">
        <div>
          <div className="text-5xl mb-4">🐾</div>
          <p className="text-slate-700">{t("finder.inactive")}</p>
        </div>
      </div>
    );
  }

  const primary = data.photos.find((p) => p.isPrimary) ?? data.photos[0];
  const submitting = finding === "submitting";
  // Grobe Plausibilität: mind. zwei Wörter + eine Ziffer (Hausnummer/PLZ).
  // Stoppt Eingaben wie "zuhause", die das Geocoding sonst irgendwo hinmappen.
  const addressPlausible = /\d/.test(address) && address.trim().split(/\s+/).length >= 2;

  // Optionale Anreicherung: Nachricht + Foto (in share & write wiederverwendet).
  const messageField = (
    <div>
      <label className="label">{t("finder.message")}</label>
      <textarea
        className="input"
        rows={3}
        maxLength={500}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t("finder.messagePlaceholder") ?? ""}
      />
    </div>
  );

  const photoField = (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
      />
      {photo && photoPreview ? (
        <div className="flex items-center gap-3 rounded-lg border border-slate-300 bg-white p-2">
          <img src={photoPreview} alt="" className="h-14 w-14 shrink-0 rounded-md object-cover" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex-1 text-left text-sm font-medium text-brand-700"
          >
            {t("finder.photoChange")}
          </button>
          <button
            type="button"
            onClick={() => setPhoto(null)}
            aria-label={t("finder.photoRemove") ?? "Foto entfernen"}
            className="shrink-0 px-1 text-2xl leading-none text-slate-400 hover:text-slate-600"
          >
            ×
          </button>
        </div>
      ) : (
        // Dezenter Pill-Button: etwas präsenter als ein reiner Textlink, aber
        // kein konkurrierender Full-Width-CTA.
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-100"
        >
          📷 {t("finder.addPhoto")}
        </button>
      )}
    </div>
  );

  // Optionale Rückrufnummer + WhatsApp-Wunsch. Freiwillig — die Anonymität
  // bleibt Standard, deshalb der klare Hinweis, dass der Besitzer die Nummer sieht.
  const contactField = (
    <div>
      <label className="label">{t("finder.phoneLabel")}</label>
      <PhoneInput value={phone} onChange={setPhone} placeholder={t("finder.phonePlaceholder") ?? ""} />
      {phone.trim() && (
        <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={whatsapp}
            onChange={(e) => setWhatsapp(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          {t("finder.whatsappOptIn")}
        </label>
      )}
      <p className="mt-1.5 text-xs text-slate-400">{t("finder.phoneHint")}</p>
    </div>
  );

  const backLink = (
    <button
      type="button"
      onClick={() => setStep("choose")}
      className="text-sm text-slate-400 underline underline-offset-2 hover:text-slate-600"
    >
      ← {t("common.back")}
    </button>
  );

  if (finding === "done") {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center p-6 text-center bg-gradient-to-br from-brand-50 to-white">
        <div className="max-w-sm">
          <div className="text-6xl mb-4">💜</div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{t("finder.successTitle")}</h1>
          <p className="mt-2 text-slate-600">{t("finder.successText")}</p>
          {distanceKm != null && (
            <p className="mt-4 inline-block rounded-full bg-brand-50 px-4 py-2 text-sm font-medium text-brand-800">
              📏{" "}
              {data.name
                ? t("finder.distance", { km: distanceKm, name: data.name })
                : t("finder.distanceNoName", { km: distanceKm })}
            </p>
          )}
        </div>

        {/* Nachreichen per PATCH ans bestehende Finding — keine zweite Meldung,
            keine doppelte Benachrichtigung des Besitzers. Einklappbar, damit die
            Distanz-Info oben nicht untergeht. */}
        {findingId && (
          <div className="mt-6 max-w-sm w-full card p-4 text-left">
            <button
              type="button"
              onClick={() => setFollowupOpen((o) => !o)}
              className="flex w-full items-center justify-between gap-2 text-left"
              aria-expanded={followupOpen}
            >
              <span className="font-semibold">
                {t("finder.followupTitle")}
                {followupSent && <span className="ml-2 text-sm font-medium text-emerald-700">✓</span>}
              </span>
              <span className="shrink-0 text-slate-400" aria-hidden>{followupOpen ? "▲" : "▼"}</span>
            </button>
            {followupOpen && (
              <>
                <p className="mt-2 text-sm text-slate-600">{t("finder.followupText")}</p>
                {followupSent && (
                  <p className="mt-2 text-sm font-medium text-emerald-700">{t("finder.followupSent")}</p>
                )}
                <div className="mt-4 space-y-4">
                  {messageField}
                  {contactField}
                  {photoField}
                  {error && <p className="text-red-600 text-sm">{error}</p>}
                  <button
                    type="button"
                    onClick={doFollowup}
                    disabled={followupSending || (!message.trim() && !photo && !phone.trim())}
                    className="w-full rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white transition-all hover:bg-brand-700 disabled:opacity-50"
                  >
                    {followupSending ? t("finder.submitting") : t("finder.followupSend")}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-6 max-w-sm w-full card p-5 text-left">
          <div className="flex items-start gap-3">
            <div className="text-3xl">🐾</div>
            <div className="flex-1">
              <div className="font-semibold">{t("finder.adTitle")}</div>
              <p className="mt-1 text-sm text-slate-600">{t("finder.adText")}</p>
              <Link to="/" className="btn-primary mt-3 inline-flex">
                {t("finder.adCta")} →
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 text-sm text-slate-500 underline underline-offset-2 hover:text-slate-700"
        >
          ← {data.name ? t("finder.backToPet", { name: data.name }) : t("finder.backToPetUnknown")}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-slate-50">
      <div className="relative">
        {primary ? (
          <button
            type="button"
            onClick={() => setPhotoExpanded(true)}
            className="relative block w-full h-56 overflow-hidden focus:outline-none bg-slate-200"
            aria-label="Foto vergrößern"
          >
            {/* Unscharfer Hintergrund füllt die Ränder — das Foto selbst wird
                nie beschnitten (object-contain), damit z.B. das Gesicht nicht
                aus dem Ausschnitt fällt. */}
            <img src={primary.url} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-60" />
            <img src={primary.url} alt="" className="relative w-full h-full object-contain" />
          </button>
        ) : (
          <div className="w-full h-44 bg-gradient-to-br from-brand-200 to-brand-500 flex items-center justify-center text-7xl">
            🐾
          </div>
        )}
      </div>

      {photoExpanded && primary && (
        <div
          role="dialog"
          onClick={() => setPhotoExpanded(false)}
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img src={primary.url} alt="" className="max-h-full max-w-full object-contain rounded-lg" />
          <button
            type="button"
            onClick={() => setPhotoExpanded(false)}
            className="absolute top-4 right-4 bg-white/90 text-slate-900 rounded-full w-10 h-10 text-2xl leading-none"
            aria-label="Schließen"
          >
            ×
          </button>
        </div>
      )}

      <div className="px-5 pt-5 pb-32">
        {/* Vermisst-Banner: erhöht die Dringlichkeit und führt zum Standort-CTA. */}
        {data.missing && (
          <div className="mb-4 flex items-start gap-2.5 rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-red-800">
            <span className="text-xl leading-none" aria-hidden>⚠️</span>
            <p className="text-[15px] font-semibold leading-snug">
              {data.name
                ? t("finder.missingBanner", { name: data.name })
                : t("finder.missingBannerUnknown")}
            </p>
          </div>
        )}
        <h1 className="font-display text-3xl font-semibold tracking-tight">
          {data.name ? t("finder.title", { name: data.name }) : t("finder.titleUnknown")}
        </h1>

        {step === "choose" && (
          <>
            {data.motto && <p className="mt-1 text-brand-700 italic">"{data.motto}"</p>}
            {data.steckbrief && (
              <p className="mt-3 text-slate-600 whitespace-pre-wrap">{data.steckbrief}</p>
            )}
            <p className="mt-5 text-slate-700">{t("finder.hello")}</p>
            <p className="mt-5 font-semibold text-slate-900">{t("finder.chooseQuestion")}</p>
            <div className="mt-4 space-y-3">
              <button
                type="button"
                onClick={startShare}
                className="w-full rounded-2xl bg-emerald-600 p-5 text-left text-white shadow-lg transition-all hover:bg-emerald-700 active:scale-[0.98]"
              >
                <div className="font-semibold text-lg">{t("finder.chooseShare")}</div>
                <div className="text-sm opacity-90 mt-0.5">{t("finder.chooseShareSub")}</div>
              </button>
              <button
                type="button"
                onClick={() => setStep("write")}
                className="w-full rounded-2xl border border-slate-300 bg-white p-4 text-left font-medium text-slate-700 transition-colors hover:border-brand-400"
              >
                {t("finder.chooseWrite")}
              </button>
            </div>
            <p className="mt-4 text-center text-xs text-slate-500">{t("finder.shareLocationHint")}</p>
          </>
        )}

        {step === "share" && (
          <div className="mt-6 space-y-5">
            {/* GPS-Status / Adress-Fallback */}
            {gps === "asking" && !coords && (
              <div className="rounded-2xl bg-white border border-slate-200 p-5 text-center text-slate-600">
                {t("finder.gpsWaiting")}
              </div>
            )}

            {coords && (
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-800 font-medium">
                {t("finder.locationCaptured")}
              </div>
            )}

            {gps === "denied" && !coords && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
                <div className="font-semibold text-amber-900">{t("finder.gpsFailedTitle")}</div>
                <p className="mt-1 text-sm text-amber-900/80">
                  {data.name
                    ? t("finder.gpsFailedText", { name: data.name })
                    : t("finder.gpsFailedTextUnknown")}
                </p>
                <input
                  className="input mt-3"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={t("finder.addressPlaceholder") ?? ""}
                  autoComplete="street-address"
                />
                {address.trim().length > 0 && !addressPlausible && (
                  <p className="mt-2 text-xs text-amber-900/80">{t("finder.addressInvalid")}</p>
                )}
                <button
                  type="button"
                  onClick={requestGps}
                  className="mt-2 text-xs text-amber-800 underline underline-offset-2"
                >
                  {t("finder.retryGps")}
                </button>
              </div>
            )}

            {/* Optionale Anreicherung */}
            <div className="space-y-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {t("finder.optionalExtras")}
              </div>
              {messageField}
              {contactField}
              {photoField}
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <button
              type="button"
              onClick={doSubmit}
              disabled={submitting || (!coords && !addressPlausible)}
              className="w-full rounded-2xl bg-brand-600 p-4 text-center text-base font-semibold text-white shadow-lg transition-all hover:bg-brand-700 active:scale-[0.98] disabled:opacity-50"
            >
              {submitting ? t("finder.submitting") : t("finder.send")}
            </button>
            <div className="text-center">{backLink}</div>
          </div>
        )}

        {step === "write" && (
          <div className="mt-6 space-y-5">
            {messageField}
            {contactField}
            {photoField}

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <button
              type="button"
              onClick={doSubmit}
              disabled={submitting || (!message.trim() && !photo && !phone.trim())}
              className="w-full rounded-2xl bg-brand-600 p-4 text-center text-base font-semibold text-white shadow-lg transition-all hover:bg-brand-700 active:scale-[0.98] disabled:opacity-50"
            >
              {submitting ? t("finder.submitting") : t("finder.send")}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={startShare}
                className="text-sm font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
              >
                {t("finder.alsoShareLocation")}
              </button>
            </div>

            <div className="text-center">{backLink}</div>
          </div>
        )}
      </div>
    </div>
  );
}
